// =============================================================================
// Authentication Service
// =============================================================================

import api from './api';
import type { User, ApiResponse } from '../types';

// =============================================================================
// Types
// =============================================================================

interface RequestCodeResponse {
  success: boolean;
  message?: string;
  error?: string;
}

interface LoginResponse {
  success: boolean;
  requiresEmail2fa?: boolean;
  // Direct login fields (when 2FA is bypassed)
  accessToken?: string;
  refreshToken?: string;
  user?: User;
  message?: string;
  error?: string;
}

interface VerifyCodeResponse {
  success: boolean;
  accessToken?: string;
  refreshToken?: string;
  user?: User;
  requiresSms2fa?: boolean;
  sessionId?: string;
  error?: string;
}

interface VerifySmsResponse {
  success: boolean;
  accessToken?: string;
  refreshToken?: string;
  user?: User;
  verifiedAt?: string;
  error?: string;
}

interface TotpSetupResponse {
  success: boolean;
  secret?: string;
  qrCodeDataUrl?: string;
  error?: string;
}

interface TotpVerifyResponse {
  success: boolean;
  message?: string;
  expiresAt?: string;
  error?: string;
}

interface TotpStatusResponse {
  success: boolean;
  enabled: boolean;
  hasValidSession: boolean;
  sessionExpiresAt?: string;
  error?: string;
}

// =============================================================================
// Auth Service
// =============================================================================

export const authService = {
  /**
   * Login with email and password
   * Returns success with requiresEmail2fa flag if password is correct
   */
  async login(
    email: string,
    password: string,
    turnstileToken?: string | null
  ): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/auth/login', {
      email,
      password,
      turnstileToken,
    });
    return response.data;
  },

  /**
   * Request email verification code
   */
  async requestEmailCode(email: string): Promise<RequestCodeResponse> {
    const response = await api.post<RequestCodeResponse>('/auth/request-code', { email });
    return response.data;
  },

  /**
   * Verify email code
   */
  async verifyEmailCode(email: string, code: string): Promise<VerifyCodeResponse> {
    const response = await api.post<VerifyCodeResponse>('/auth/verify-code', { email, code });
    return response.data;
  },

  /**
   * Request SMS 2FA code
   */
  async requestSmsCode(purpose: 'login' | 'sensitive_data' = 'sensitive_data'): Promise<RequestCodeResponse> {
    const response = await api.post<RequestCodeResponse>('/auth/request-sms-code', { purpose });
    return response.data;
  },

  /**
   * Verify SMS 2FA code
   */
  async verifySmsCode(
    code: string,
    options?: { sessionId?: string; purpose?: string }
  ): Promise<VerifySmsResponse> {
    const response = await api.post<VerifySmsResponse>('/auth/verify-sms-code', {
      code,
      ...options,
    });
    return response.data;
  },

  /**
   * Setup phone for 2FA
   */
  async setupPhone(phone: string): Promise<RequestCodeResponse> {
    const response = await api.post<RequestCodeResponse>('/auth/setup-phone', { phone });
    return response.data;
  },

  /**
   * Verify phone and enable 2FA
   */
  async verifyPhone(phone: string, code: string): Promise<ApiResponse> {
    const response = await api.post<ApiResponse>('/auth/verify-phone', { phone, code });
    return response.data;
  },

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken?: string): Promise<{ accessToken?: string; error?: string }> {
    // Refresh token is primarily sent via HttpOnly cookie (withCredentials: true on api instance).
    // Body token is a backward-compatibility fallback for pre-cookie-fix sessions.
    const response = await api.post<{ success: boolean; accessToken?: string; error?: string }>(
      '/auth/refresh',
      refreshToken ? { refreshToken } : {}
    );
    return response.data;
  },

  /**
   * Logout
   */
  async logout(): Promise<void> {
    await api.post('/auth/logout');
  },

  /**
   * Get current user
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await api.get<{ success: boolean; user?: User }>('/auth/me');
      return response.data.user || null;
    } catch {
      return null;
    }
  },

  // ===========================================================================
  // Password Reset
  // ===========================================================================

  /**
   * Request password reset code
   */
  async requestPasswordReset(email: string): Promise<RequestCodeResponse> {
    const response = await api.post<RequestCodeResponse>('/auth/forgot-password', { email });
    return response.data;
  },

  /**
   * Verify password reset code
   */
  async verifyPasswordResetCode(
    email: string,
    code: string
  ): Promise<{ success: boolean; resetToken?: string; error?: string }> {
    const response = await api.post<{ success: boolean; resetToken?: string; error?: string }>(
      '/auth/verify-reset-code',
      { email, code }
    );
    return response.data;
  },

  /**
   * Complete password reset
   */
  async resetPassword(
    email: string,
    resetToken: string,
    newPassword: string
  ): Promise<RequestCodeResponse> {
    const response = await api.post<RequestCodeResponse>('/auth/reset-password', {
      email,
      resetToken,
      newPassword,
    });
    return response.data;
  },

  // ===========================================================================
  // Account Activation
  // ===========================================================================

  /**
   * Activate account using secure token from welcome email
   */
  async activateAccount(
    token: string,
    newPassword: string
  ): Promise<RequestCodeResponse> {
    const response = await api.post<RequestCodeResponse>('/auth/activate-account', {
      token,
      newPassword,
    });
    return response.data;
  },

  // ===========================================================================
  // TOTP (Microsoft Authenticator)
  // ===========================================================================

  /**
   * Initiate TOTP setup - get QR code for scanning
   */
  async initiateTotpSetup(): Promise<TotpSetupResponse> {
    const response = await api.post<TotpSetupResponse>('/auth/totp/setup');
    return response.data;
  },

  /**
   * Complete TOTP setup by verifying first code
   */
  async completeTotpSetup(code: string): Promise<TotpVerifyResponse> {
    const response = await api.post<TotpVerifyResponse>('/auth/totp/verify-setup', { code });
    return response.data;
  },

  /**
   * Verify TOTP code for sensitive data access
   */
  async verifyTotp(code: string): Promise<TotpVerifyResponse> {
    const response = await api.post<TotpVerifyResponse>('/auth/totp/verify', { code });
    return response.data;
  },

  /**
   * Get TOTP status for current user
   */
  async getTotpStatus(): Promise<TotpStatusResponse> {
    const response = await api.get<TotpStatusResponse>('/auth/totp/status');
    return response.data;
  },

  /**
   * Reset TOTP - get new QR code for users who lost their authenticator
   */
  async resetTotp(): Promise<TotpSetupResponse> {
    const response = await api.post<TotpSetupResponse>('/auth/totp/reset');
    return response.data;
  },
};

export default authService;
